// Realtime subscription for one trip.
//
// GET /realtime?tripId=... upgraded to a WebSocket. Read only: per the
// contract the client sends nothing but `ping`, every write goes over HTTP
// so the offline queue has exactly one retry path. Reconnects with capped
// exponential backoff and a small jitter so a server restart does not bring
// every client back in the same millisecond.
import { apiClient } from "./api";
import type { RealtimeFrame } from "./types";

export interface RealtimeHandle {
  close(): void;
}

export interface RealtimeOptions {
  onFrame(frame: RealtimeFrame): void;
  onStatus?(connected: boolean): void;
}

const PING_INTERVAL_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

/**
 * `tripId` null subscribes to the PUBLIC presence topic, which is what the
 * live map needs: every traveller, whatever trip they happen to be on.
 */
export function subscribeRealtime(
  tripId: string | null,
  options: RealtimeOptions,
): RealtimeHandle {
  let socket: WebSocket | null = null;
  let closed = false;
  let attempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;

  function cleanup(): void {
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = null;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
    }
  }

  function scheduleReconnect(): void {
    if (closed) return;
    attempts += 1;
    const base = Math.min(MAX_BACKOFF_MS, 500 * 2 ** Math.min(attempts, 6));
    const delay = base + Math.floor(Math.random() * 400);
    reconnectTimer = setTimeout(connect, delay);
  }

  function connect(): void {
    if (closed) return;
    if (typeof WebSocket === "undefined") return;
    try {
      socket = new WebSocket(apiClient.realtimeUrl(tripId));
    } catch {
      scheduleReconnect();
      return;
    }

    socket.onopen = () => {
      attempts = 0;
      options.onStatus?.(true);
      pingTimer = setInterval(() => {
        try {
          socket?.send(JSON.stringify({ type: "ping" }));
        } catch {
          // Socket already gone, onclose handles the reconnect.
        }
      }, PING_INTERVAL_MS);
    };

    socket.onmessage = (event: MessageEvent) => {
      if (typeof event.data !== "string") return;
      let frame: RealtimeFrame;
      try {
        frame = JSON.parse(event.data) as RealtimeFrame;
      } catch {
        return;
      }
      if (!frame || typeof frame.type !== "string") return;
      options.onFrame(frame);
    };

    socket.onerror = () => {
      // onclose always follows, reconnect is handled there.
    };

    socket.onclose = () => {
      cleanup();
      options.onStatus?.(false);
      scheduleReconnect();
    };
  }

  connect();

  return {
    close() {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      cleanup();
      try {
        socket?.close();
      } catch {
        // Already closed.
      }
      socket = null;
    },
  };
}
